import { useMediaTempCreate } from "@/api/media";
import { useHPTARValidate } from "@/api/rfs";
import { HPTARFormSchema } from "../HPTARCreateForm";
import { swalError, swalLoading, swalSuccess } from "@/utils/lib/swal";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";
import {
  useHPTARGeneralMessage,
  useInfiniteHPTARInvalids,
  useInfiniteHPTARs,
} from "@/api/hptar";

export default function useCreateHPTAR({ id }: { id: string | undefined }) {
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { mutate: mutateMedia } = useMediaTempCreate();
  const { mutate: mutateHPTAR } = useHPTARValidate();
  const { refetch: refetchHPTARs } = useInfiniteHPTARs({ rfsId: id });

  const { refetch: refetchGeneralMessages } = useHPTARGeneralMessage({
    rfsId: id,
  });
  const { refetch: refetchHPTARInvalids } = useInfiniteHPTARInvalids({
    rfsId: id,
  });

  function handleSubmit(values: HPTARFormSchema) {
    if (
      values.fileList &&
      values.fileList.length > 0 &&
      !values.hptar?.isHptarNoUpdated
    ) {
      swalLoading.fire();
      mutateMedia(
        {
          data: {
            file: values?.fileList?.[0],
            target: "/hptar-document",
          },
        },
        {
          onSuccess: (result) => {
            if (!id) {
              throw new Error(MISSING_ID_IN_ROUTE);
            }
            mutateHPTAR(
              {
                id,
                data: {
                  hptar: {
                    media: {
                      id: result.id,
                      path: result.path,
                      originalFileName: result.originalFileName,
                      name: result.name,
                    },
                  },
                },
              },
              {
                onSuccess: onSuccess,
                onError: onError,
                onSettled: onSettled,
              },
            );
          },
        },
      );
    } else {
      swalLoading.fire();

      if (id) {
        mutateHPTAR(
          {
            id,
            data: {
              hptar: {
                isHptarNoUpdated: values.hptar?.isHptarNoUpdated ?? false,
              },
            },
          },
          {
            onSuccess: onSuccess,
            onError: onError,
            onSettled: onSettled,
          },
        );
      }
    }
  }

  function onSuccess() {
    swalSuccess.fire({
      title: "Validation Successful",
      text: "You have successfully submitted the HPTAR",
    });
  }

  function onError() {
    swalError.fire({
      title: "Validation Failed",
      text: "You failed to perform data validation. Please check the HPTAR (.xlsx) and try again.",
    });
  }

  function onSettled() {
    refetchHPTARs();
    refetchHPTARInvalids();
    refetchGeneralMessages();
  }

  return { handleSubmit };
}
